import { useEffect, useState } from 'react';
import { Table, Empty, Button } from 'antd';
import { TableToolbar } from '../ui/TableToolbar.js';
import { SkeletonPage } from '../ui/SkeletonPage.js';
import { useUrlPagination } from '../hooks/useUrlPagination.js';
import { usePortalCtx } from '../context.js';
import { apiGet } from '../api.js';
import type { Paginated } from '../types/pagination.js';

interface MetricRow {
  id: string;
  siteKey: string;
  metric: string;
  value: number;
  windowStart: string;
  windowEnd: string;
}

const ENDPOINT = '/admin/analytics';

export default function AnalyticsList() {
  const { registryUrl, siteKey } = usePortalCtx();
  const { page, pageSize, q, setPage, setPageSize } = useUrlPagination();
  const [data, setData] = useState<Paginated<MetricRow> | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const url = new URL(ENDPOINT, registryUrl);
    url.searchParams.set('page', String(page));
    url.searchParams.set('pageSize', String(pageSize));
    if (q) url.searchParams.set('q', q);
    if (siteKey) url.searchParams.set('siteKey', siteKey);
    (async () => {
      try {
        const resp = await apiGet<Paginated<MetricRow>>(url.toString());
        if (!cancelled) setData(resp);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [registryUrl, siteKey, page, pageSize, q]);

  if (loading) return <SkeletonPage />;
  const rows = data?.items ?? [];
  const total = data?.total ?? 0;
  if (!rows.length && !q) return <Empty description="No analytics yet" />;

  return (
    <>
      <TableToolbar
        placeholder="Search analytics"
        extra={<Button onClick={() => location.reload()}>Refresh</Button>}
      />
      <Table<MetricRow>
        rowKey="id"
        sticky
        size="small"
        dataSource={rows}
        columns={[
          { title: 'Metric', dataIndex: 'metric' },
          { title: 'Value', dataIndex: 'value' },
          { title: 'Site', dataIndex: 'siteKey' },
          {
            title: 'Window',
            render: (_, r) =>
              `${new Date(r.windowStart).toLocaleString()} → ${new Date(r.windowEnd).toLocaleString()}`,
          },
        ]}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          onChange: (p, s) => {
            setPage(p);
            if (s !== pageSize) setPageSize(s);
          },
        }}
      />
    </>
  );
}
